import React from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import validate from "validate.js";
import uuidv4 from "uuid/v4";
import { Input, PlainTextInput } from "components/Inputs";
import { Button, ModalHeader } from "components/Shared";
import FormSelector from "v2/components/Forms/Selector";

const constraints = {
	name: {
		presence: true
	},
	kind: {
		presence: true
	}
};

export default class ConditionModal extends React.Component {
	constructor(props) {
		super(props);
		// Directly assign initial state instead of calling setState
		this.state = this.getStateFromProps(props);
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		// Only update if modal is opening or item changed
		if (nextProps.isOpen && !this.props.isOpen) {
			this.setState(this.getStateFromProps(nextProps));
		}
	}

	getStateFromProps(props) {
		// Return state object instead of calling setState
		if (props.item) {
			const id = props.item.id ? props.item.id : uuidv4();
			const { errors, item } = props;
			const { name, description, kind, form_id, form_code, form_name } = item;
			return {
				id,
				name,
				description,
				kind,
				form_id: form_id || null,
				form_code: form_code || null,
				form_name: form_name || null,
				errors
			};
		} else {
			return {
				id: uuidv4(),
				name: "",
				description: "",
				kind: "",
				form_id: null,
				form_code: null,
				form_name: null
			};
		}
	}

	doCloseModal() {
		const { doCloseModal } = this.props;
		if (doCloseModal) {
			// Reset state when closing
			this.setState({
				id: uuidv4(),
				name: "",
				description: "",
				kind: "",
				form_id: null,
				form_code: null,
				form_name: null
			});
			doCloseModal();
		}
	}

	onDelete(event) {
		const { onDelete } = this.props;
		event.preventDefault();
		if (onDelete) {
			onDelete(this.state.id);
			this.doCloseModal();
		}
	}

	isValid() {
		const data = Object.assign({}, this.state);
		const errors = validate(data, constraints);
		if (errors !== undefined) {
			this.setState({ errors });
			return false;
		}
		// Enforce: at most one submission condition per task.
		// If the selected kind is "submission" and there is already a submission
		// condition in the existing list that is NOT the one currently being edited
		// (matched by id), block the save with the canonical error string.
		if (data.kind === "submission") {
			const existingConditions = this.props.existingConditions || [];
			const alreadyHasSubmission = existingConditions.some(
				(c) => c.kind === "submission" && !c.deleted_at && c.id !== data.id
			);
			if (alreadyHasSubmission) {
				this.setState({
					errors: { kind: ["Only one submission condition is allowed per task"] }
				});
				return false;
			}
		}
		return true;
	}

	onSave(event) {
		event.preventDefault();
		const { onUpdate } = this.props;
		if (onUpdate && this.isValid()) {
			onUpdate(this.state);
			this.doCloseModal();
		}
	}

	render() {
		const { isOpen, doCloseModal } = this.props;
		return (
			<Modal
				isOpen={isOpen}
				onRequestClose={doCloseModal}
				className="condition-modal"
				contentLabel="condition"
				label="Condition"
			>
				<ModalHeader title="condition" onRequestClose={doCloseModal} />
				<section>
					<Input
						large
						required={constraints}
						hint="This will list the name of the document you want uploaded"
						limit={155}
						name="name"
						defaultState={this.state}
						onChange={name => this.setState({ name })}
					/>
					<Input
						name="kind"
						required={constraints}
						defaultState={this.state}
						onChange={kind => this.setState({
							kind,
							form_id: kind === "create_form" ? this.state.form_id : null,
							form_code: kind === "create_form" ? this.state.form_code : null,
							form_name: kind === "create_form" ? this.state.form_name : null,
							// Clear the submission-duplicate error when the user changes the kind
							errors: this.state.errors && this.state.errors.kind
								? Object.assign({}, this.state.errors, { kind: undefined })
								: this.state.errors
						})}
						options={[
							{ label: "Create Form", value: "create_form" },
							{ label: "Upload a Document", value: "document" },
							{ label: "Submission", value: "submission" },
							{ label: "Boolean", value: "boolean" },
							{ label: "Date", value: "date" },
							{ label: "Accept", value: "accept" }
						]}
					/>
					<PlainTextInput
						hint="Do you need to tell the Role responsible anything specific about this document? E.g. 'Make sure this is an executed copy'"
						limit={300}
						name="description"
						defaultState={this.state}
						onChange={description => this.setState({ description })}
					/>
					{this.state.kind === "create_form" && (
						<div className="form-group">
							<label>Form Template</label>
							<FormSelector
								propertyId={this.props.propertyId}
								value={this.state.form_id}
								onChange={(form_id, form) =>
									this.setState({
										form_id,
										// Store the clean, suffix-stripped code (and the filename)
										// so the condition list shows the filename, not the random
										// upload suffix. Falls back to raw code if display_code is absent.
										form_code: form ? form.display_code || form.code : null,
										form_name: form ? form.name : null
									})
								}
							/>
							<small className="form-text text-muted">
								Pick the .docx form from the property library. Its placeholders will be filled when the task is assigned.
							</small>
						</div>
					)}
				</section>
				<footer>
					<Button onClick={event => this.onSave(event)} />
				</footer>
			</Modal>
		);
	}
}

ConditionModal.propTypes = {
	onUpdate: PropTypes.func,
	doCloseModal: PropTypes.func,
	// The condition being edited. Absent/null opens the modal blank for Add;
	// supplying one prefills every field and KEEPS ITS ID, which is what lets the
	// caller replace the row instead of appending a copy.
	item: PropTypes.object,
	existingConditions: PropTypes.array,
	// The template's configured property (api-contract §1.3). Scopes the Form
	// Template picker (FormSelector) to that property's forms. Undefined when
	// the template has no property configured yet — FormSelector then falls
	// back to its existing grouped-by-property behavior.
	propertyId: PropTypes.string
};
